import {
  Button,
  Chip,
  Group,
  Stack,
  Stepper,
  Switch,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconDog, IconUser } from "@tabler/icons-react";
import { fetchAuthSession } from "aws-amplify/auth";
import { format } from "date-fns";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useEffect, useState } from "react";
import { client } from "@/api-client";
import type { DogInput, OwnerProfileInput } from "@/api-types";
import { Header } from "@/components/Header";
import { YearMonthSelect } from "@/components/YearMonthSelect";
import { initFCMPassive } from "@/fcm";
import { UniversalRoutes, useNavigator } from "@/routers/routes";
import { useUser } from "@/UserContext";
import { energyLevelOptions, temperamentOptions } from "./common";
import { dogProfileSchema, ownerProfileSchema } from "./schemas";

// Types are now imported directly from @/api-types

export const NewProfile = () => {
  const [active, setActive] = useState(0);
  const { navigate } = useNavigator();
  const user = useUser();

  useEffect(() => {
    if (user.isProfileCreated) {
      navigate({ path: UniversalRoutes.HOME });
    }
  }, [user.isProfileCreated, navigate]);

  // Generate year options (from current year back to 30 years ago)
  const _currentYear = new Date().getFullYear();

  const ownerForm = useForm({
    initialValues: {
      firstName: "",
      lastName: "",
      phone: "",
      locality: "",
      news: false,
    },
    validate: zod4Resolver(ownerProfileSchema),
  });

  const dogForm = useForm({
    initialValues: {
      name: "",
      breed: "",
      birthDate: null as Date | null,
      energyLevel: "medium",
      temperament: "neutral",
      trainingNeeds: "",
      healthConcerns: "",
      sterilised: false,
      medication: "",
    },
    validate: zod4Resolver(dogProfileSchema),
  });

  const updateOwnerProfile = client.useMutation("post", "/owner/profile/");

  const handleSubmit = async () => {
    const ownerValues = ownerForm.values;
    const dogValues = dogForm.values;

    // biome-ignore lint/style/noNonNullAssertion: there is a validation
    const date = format(dogValues.birthDate!, "yyyy-MM-dd");

    const dogInput: DogInput = {
      name: dogValues.name,
      breed: dogValues.breed,
      birthDate: date,
      energyLevel: dogValues.energyLevel,
      temperament: dogValues.temperament,
      trainingNeeds: dogValues.trainingNeeds || null,
      healthConcerns: dogValues.healthConcerns || null,
      sterilised: dogValues.sterilised,
      medication: dogValues.medication || null,
    };

    const ownerInput: OwnerProfileInput = {
      ...ownerValues,
      dogs: [dogInput],
    };

    await updateOwnerProfile.mutateAsync({
      body: ownerInput,
    });

    await fetchAuthSession({ forceRefresh: true });
    await initFCMPassive();
  };

  const nextStep = () => {
    if (active === 0) {
      dogForm.validate();
      if (dogForm.isValid()) {
        setActive((current) => current + 1);
      }
    } else {
      ownerForm.validate();
      if (ownerForm.isValid()) {
        handleSubmit();
      }
    }
  };

  const prevStep = () => setActive((current) => current - 1);

  return (
    <Stack>
      <Header title="Create Your Profile" />
      <Stepper active={active} orientation="vertical">
        <Stepper.Step
          label="About your dog"
          description="Tell us about your dog"
          icon={<IconDog />}
        >
          <form onSubmit={dogForm.onSubmit(nextStep)} id="dog-form">
            <Stack>
              <TextInput
                withAsterisk
                label="Dog's Name"
                {...dogForm.getInputProps("name")}
              />
              <TextInput
                withAsterisk
                label="Dog's Breed"
                {...dogForm.getInputProps("breed")}
              />
              <YearMonthSelect
                {...dogForm.getInputProps("birthDate")}
                label="Dog's birth year and month"
              />
              <Stack gap="xs">
                <label htmlFor="energyLevel">Energy Level</label>
                <Chip.Group
                  multiple={false}
                  {...dogForm.getInputProps("energyLevel")}
                >
                  <Group>
                    {energyLevelOptions.map((option) => (
                      <Chip key={option.value} value={option.value}>
                        {option.label}
                      </Chip>
                    ))}
                  </Group>
                </Chip.Group>
              </Stack>
              <Stack gap="xs">
                <label htmlFor="temperament">Temperament</label>
                <Chip.Group
                  multiple={false}
                  {...dogForm.getInputProps("temperament")}
                >
                  <Group>
                    {temperamentOptions.map((option) => (
                      <Chip key={option.value} value={option.value}>
                        {option.label}
                      </Chip>
                    ))}
                  </Group>
                </Chip.Group>
              </Stack>
              <Textarea
                label="Training Needs"
                placeholder="Describe any training needs or goals"
                {...dogForm.getInputProps("trainingNeeds")}
              />
              <Textarea
                label="Health Concerns"
                placeholder="Describe any health concerns or conditions"
                {...dogForm.getInputProps("healthConcerns")}
              />

              <Switch
                label="Sterilised / Neutered"
                description="Is your dog sterilised or neutered?"
                {...dogForm.getInputProps("sterilised", { type: "checkbox" })}
              />

              <Textarea
                label="Medication"
                placeholder="List any medications your dog is currently taking (leave empty if none)"
                {...dogForm.getInputProps("medication")}
              />
            </Stack>
          </form>
        </Stepper.Step>

        <Stepper.Step
          label="About you"
          description="Few questions about you"
          icon={<IconUser />}
        >
          <form onSubmit={ownerForm.onSubmit(handleSubmit)} id="owner-form">
            <Stack>
              <TextInput
                withAsterisk
                id="firstName"
                name="firstName"
                autoComplete="given-name"
                label="First name"
                {...ownerForm.getInputProps("firstName")}
              />
              <TextInput
                withAsterisk
                id="lastName"
                name="lastName"
                autoComplete="family-name"
                label="Last name"
                {...ownerForm.getInputProps("lastName")}
              />
              <TextInput
                withAsterisk
                id="phone"
                name="phone"
                autoComplete="tel"
                label="Phone"
                {...ownerForm.getInputProps("phone")}
              />
              <TextInput
                withAsterisk
                id="city"
                name="city"
                autoComplete="address-level2"
                label="City"
                {...ownerForm.getInputProps("locality")}
              />
            </Stack>
          </form>
        </Stepper.Step>
      </Stepper>

      <Group justify="flex-end" mt="xl">
        {active !== 0 && (
          <Button
            variant="default"
            onClick={prevStep}
            disabled={updateOwnerProfile.isPending}
          >
            Back
          </Button>
        )}
        <Button
          onClick={nextStep}
          loading={active === 1 ? updateOwnerProfile.isPending : false}
        >
          {active === 1 ? "Create Profile" : "Next"}
        </Button>
      </Group>
    </Stack>
  );
};
